import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, Volume2, VolumeX, Plus, ArrowLeft, Share2,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/content/landing";
import { LanguageSwitcher } from "@/i18n/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD } = BRAND.colors;

function PromoCard({ promo, isActive, isAdjacent, muted, onToggleMute, onLike, liked, onCTAClick }) {
  const videoRef = useRef(null);
  const [viewed, setViewed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      if (!viewed) {
        setViewed(true);
        axios.post(`${BACKEND}/api/promos/${promo.promo_id}/view`).catch(() => {});
      }
    } else {
      v.pause();
    }
  }, [isActive, promo.promo_id, viewed]);

  const videoSrc = promo.video_url?.startsWith("http") ? promo.video_url : `${BACKEND}${promo.video_url}`;
  // Only active + immediately-adjacent cards prefetch the full video.
  // Far-away cards only fetch metadata so initial page payload stays small.
  const preload = isActive ? "auto" : (isAdjacent ? "metadata" : "none");

  return (
    <div
      className="snap-start relative w-full h-[100dvh] flex items-center justify-center bg-black overflow-hidden"
      data-testid={`promo-card-${promo.promo_id}`}
    >
      {/* Loading skeleton — visible until the active video has a first frame */}
      {isActive && !ready && (
        <div className="absolute inset-0 grid place-items-center" data-testid={`promo-skeleton-${promo.promo_id}`} style={{ background: "linear-gradient(135deg, #1a1a1d 0%, #09090b 70%)" }}>
          <div className="flex flex-col items-center gap-3 text-white/40">
            <div className="w-14 h-14 grid place-items-center bg-white/5 border border-white/10">
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-semibold">Loading video…</div>
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        src={videoSrc}
        loop
        playsInline
        muted={muted}
        preload={preload}
        onLoadedData={() => setReady(true)}
        onCanPlay={() => setReady(true)}
        className={`w-full h-full object-cover transition-opacity duration-300 ${ready ? "opacity-100" : "opacity-0"}`}
        onClick={() => {
          const v = videoRef.current;
          if (!v) return;
          if (v.paused) v.play().catch(() => {});
          else v.pause();
        }}
      />

      {/* gradient overlays */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

      {/* top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-5 pt-5">
        <Link to="/" className="text-white/90 hover:text-white inline-flex items-center gap-2 text-sm font-semibold" data-testid="tv-back-home">
          <ArrowLeft className="w-5 h-5" /> Home
        </Link>
        <div className="flex items-center gap-2">
          <div className="bg-white/10 backdrop-blur border border-white/15 px-1">
            <LanguageSwitcher />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/80 font-semibold inline-flex items-center gap-1.5 bg-white/10 backdrop-blur px-3 py-1.5 border border-white/15">
            <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} />
            Opportunity TV
          </div>
          <button
            onClick={onToggleMute}
            className="w-10 h-10 grid place-items-center bg-white/10 backdrop-blur border border-white/15 text-white hover:bg-white/20"
            data-testid="tv-mute-toggle"
            aria-label="Toggle mute"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* right action rail */}
      <div className="absolute right-3 bottom-32 z-10 flex flex-col items-center gap-5">
        <button
          onClick={() => onLike(promo)}
          className="flex flex-col items-center gap-1 group"
          data-testid={`tv-like-${promo.promo_id}`}
        >
          <span className={`w-12 h-12 grid place-items-center transition-all ${liked ? "bg-[#C8102E] text-white" : "bg-white/15 backdrop-blur border border-white/20 text-white group-hover:bg-white/25"}`}>
            <Heart className={`w-5 h-5 ${liked ? "fill-current" : ""}`} />
          </span>
          <span className="text-white text-xs font-bold drop-shadow">{promo.likes || 0}</span>
        </button>
        <button
          onClick={() => {
            if (navigator.share) {
              navigator.share({ title: promo.title, text: promo.caption, url: window.location.href }).catch(() => {});
            } else {
              navigator.clipboard.writeText(window.location.href);
            }
          }}
          className="flex flex-col items-center gap-1 group"
          data-testid={`tv-share-${promo.promo_id}`}
        >
          <span className="w-12 h-12 grid place-items-center bg-white/15 backdrop-blur border border-white/20 text-white group-hover:bg-white/25">
            <Share2 className="w-5 h-5" />
          </span>
          <span className="text-white text-[10px] font-semibold uppercase tracking-wider drop-shadow">Share</span>
        </button>
      </div>

      {/* bottom content */}
      <div className="absolute left-0 right-20 bottom-0 z-10 p-5 pb-7 text-white">
        <div className="flex items-center gap-3 mb-3">
          {promo.owner_picture ? (
            <img src={promo.owner_picture} alt="" className="w-10 h-10 object-cover border-2 border-white" />
          ) : (
            <div className="w-10 h-10 grid place-items-center bg-zinc-700 text-white text-xs font-bold border-2 border-white">
              {(promo.owner_name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="leading-tight">
            <div className="font-bold text-sm">@{(promo.owner_name || "user").split(" ")[0].toLowerCase()}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">
              {(promo.owner_role || "member").replace("_", " ")}
            </div>
          </div>
          {promo.brand_tag ? (
            <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 bg-white/15 backdrop-blur border border-white/20">
              #{promo.brand_tag}
            </span>
          ) : null}
        </div>

        <h2 className="font-display font-black text-2xl leading-tight tracking-tight" data-testid={`tv-title-${promo.promo_id}`}>
          {promo.title}
        </h2>
        {promo.caption ? (
          <p className="mt-2 text-sm text-white/90 max-w-md leading-snug">{promo.caption}</p>
        ) : null}

        {/* Revenue Action Row (Sprint 2D — MIRESCO TV Revenue Overlay).
             Shows Buy/Affiliate/Agent/Stockist/Apply/WhatsApp when the promo is linked to a business opportunity. */}
        <TvRevenueRow promo={promo} onGenericCTA={() => onCTAClick(promo)} />
      </div>
    </div>
  );
}

// TvRevenueRow — mobile-first action grid; derives target opportunity slug from cta_url or promo.opportunity_slug.
function TvRevenueRow({ promo, onGenericCTA }) {
  const [opp, setOpp] = React.useState(null);
  const slug = React.useMemo(() => {
    if (promo.opportunity_slug) return promo.opportunity_slug;
    const m = (promo.cta_url || "").match(/\/opportunity\/([\w-]+)/);
    return m ? m[1] : "";
  }, [promo]);

  React.useEffect(() => {
    if (!slug) return;
    axios.get(`${BACKEND}/api/public/business-opportunities/${slug}`)
      .then((r) => setOpp(r.data)).catch(() => setOpp(null));
  }, [slug]);

  if (!slug || !opp) {
    // fallback to generic single CTA
    return (
      <button
        onClick={onGenericCTA}
        className="mt-4 inline-flex items-center gap-2 text-white px-5 py-3 font-bold text-sm hover:opacity-90"
        style={{ background: RED }}
        data-testid={`tv-cta-${promo.promo_id}`}
      >
        {promo.cta_label || "Learn More"}
        <Sparkles className="w-4 h-4" />
      </button>
    );
  }

  const wa = (opp.whatsapp_number || "60137003381").replace(/\D/g, "");
  const openWA = (tpl) => window.open(`https://wa.me/${wa}?text=${encodeURIComponent(tpl || "Hi MIRESCO!")}`, "_blank", "noopener");
  const kindStyle = {
    buy:       { bg: RED,      label: "Buy" },
    affiliate: { bg: GOLD,     label: "Affiliate" },
    agent:     { bg: "#1A1A1A",label: "Agent" },
    stockist:  { bg: "#0e7c66",label: "Stockist" },
    apply:     { bg: RED,      label: "Apply" },
    whatsapp:  { bg: "#25D366",label: "WhatsApp" },
  };
  const ctas = (opp.ctas || []).filter((c) => c.visible !== false).slice(0, 5);

  return (
    <div className="mt-4 space-y-2 max-w-md" data-testid={`tv-revenue-row-${promo.promo_id}`}>
      <div className="grid grid-cols-2 gap-1.5">
        {ctas.map((c, i) => {
          const st = kindStyle[c.kind] || { bg: "#1A1A1A", label: c.label };
          return (
            <button
              key={i}
              onClick={() => openWA(c.wa_template || `Hi ${opp.brand || "MIRESCO"}! ${c.label} — ${opp.title}`)}
              data-testid={`tv-cta-${promo.promo_id}-${c.kind}`}
              className="text-white text-xs font-semibold px-2 py-2 hover:opacity-90 flex items-center justify-center gap-1"
              style={{ background: st.bg }}
            >
              {c.label || st.label}
            </button>
          );
        })}
      </div>
      <a
        href={`/opportunity/${slug}`}
        className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white underline"
        data-testid={`tv-learn-${promo.promo_id}`}
      >
        Learn more about {opp.brand || opp.title} <Sparkles className="w-3 h-3" />
      </a>
    </div>
  );
}

function EmptyState({ onPost, user }) {
  return (
    <div className="snap-start relative w-full h-[100dvh] flex items-center justify-center bg-black text-white">
      <div className="text-center max-w-md px-6">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 backdrop-blur border border-white/15">
          <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} />
          Opportunity TV
        </div>
        <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Be the first to <span style={{ color: GOLD }}>go viral</span>.
        </h1>
        <p className="mt-4 text-white/80 text-base">
          Post a 60-second video to promote your product, brand or opportunity. Reach Malaysians ready to buy and earn.
        </p>
        <button
          onClick={onPost}
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 font-bold text-sm hover:opacity-90"
          style={{ background: RED, color: "white" }}
          data-testid="tv-empty-post-cta"
        >
          <Plus className="w-4 h-4" /> {user ? "Post Your First Promo" : "Sign In & Post"}
        </button>
      </div>
    </div>
  );
}

export default function OpportunityTV() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [promos, setPromos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [likedSet, setLikedSet] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/promos/feed?limit=30`);
      setPromos(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Scroll-snap detection — use the container's own client height so we don't
  // mis-detect when 100dvh differs from window.innerHeight (mobile Safari URL bar).
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const onScroll = () => {
      const h = c.clientHeight || window.innerHeight || 1;
      const idx = Math.round(c.scrollTop / h);
      if (idx !== activeIndex) setActiveIndex(idx);
    };
    c.addEventListener("scroll", onScroll, { passive: true });
    return () => c.removeEventListener("scroll", onScroll);
  }, [activeIndex]);

  const handleLike = async (promo) => {
    if (!user) {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/tv")}`;
      return;
    }
    try {
      const { data } = await axios.post(`${BACKEND}/api/promos/${promo.promo_id}/like`);
      setPromos((prev) => prev.map((p) => p.promo_id === promo.promo_id ? { ...p, likes: data.likes } : p));
      setLikedSet((prev) => {
        const next = new Set(prev);
        if (data.liked) next.add(promo.promo_id); else next.delete(promo.promo_id);
        return next;
      });
    } catch {/* ignore */}
  };

  const handleCTA = (promo) => {
    if (promo.cta_url) {
      const isExternal = /^https?:/i.test(promo.cta_url);
      if (isExternal) window.open(promo.cta_url, "_blank", "noopener");
      else navigate(promo.cta_url);
    } else if (promo.opportunity_id) {
      navigate("/opportunities");
    } else {
      navigate("/opportunities");
    }
  };

  const onPost = () => {
    if (!user) {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/post-promo")}`;
    } else {
      navigate("/post-promo");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-white text-sm" data-testid="tv-loading">
        Loading feed…
      </div>
    );
  }

  return (
    <div className="relative bg-black">
      {/* floating post button */}
      <button
        onClick={onPost}
        className="fixed bottom-6 right-6 z-30 w-14 h-14 grid place-items-center text-white shadow-2xl hover:scale-105 transition-transform"
        style={{ background: RED }}
        data-testid="tv-floating-post-btn"
        aria-label="Post a promo"
      >
        <Plus className="w-6 h-6" />
      </button>

      <div
        ref={containerRef}
        className="h-[100dvh] overflow-y-scroll snap-y snap-mandatory"
        style={{ scrollbarWidth: "none" }}
        data-testid="tv-feed-container"
      >
        {promos.length === 0 ? (
          <EmptyState onPost={onPost} user={user} />
        ) : (
          promos.map((p, i) => (
            <PromoCard
              key={p.promo_id}
              promo={p}
              isActive={i === activeIndex}
              isAdjacent={Math.abs(i - activeIndex) === 1}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              onLike={handleLike}
              liked={likedSet.has(p.promo_id)}
              onCTAClick={handleCTA}
            />
          ))
        )}
      </div>
    </div>
  );
}
